// A/B test resolution provider — fetches the PUBLIC abTestResolutions overlay
// (testId → winning variant) and applies it imperatively into the
// abTestingService singleton so assignVariant (arm 2b) routes resolved tests to
// their winner ahead of the sticky cookie. Mirrors RuntimeFlagsProvider's
// proven shape: a module-level store + fetchQuery → applyServerOverlays. NO
// Suspense / useLazyLoadQuery (that combo is this repo's documented
// store-and-network infinite-refetch trap; a suspending provider would also
// collapse the whole funnel to its fallback on each refetch).
//
// This renders for BOTH the funnel and the admin app (mounted inside
// ABTestProvider at the root). It fetches a PUBLIC query, so it works for
// anonymous funnel visitors. Fail-open: on any error the overlay stays empty
// and assignment falls back to live config hashing — the funnel never breaks.
//
// Refresh policy: on mount if the store is empty; on visibilitychange→visible
// past a 60s TTL; and after endAbTest / reopenAbTest mutations (call
// invalidateAbTestResolutions() from their onCompleted callbacks). No polling.

import { ReactNode, useEffect } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";
import { abTestingService } from "../../utils/abTestingService";
import type { ABTestResolutionProviderQuery } from "./__generated__/ABTestResolutionProviderQuery.graphql";

const RESOLUTIONS_QUERY = graphql`
  query ABTestResolutionProviderQuery {
    abTestResolutions {
      testId
      resolvedVariant
    }
  }
`;

// Module-level store — outlives DashboardContainer / route remounts so we don't
// refetch on every navigation. `environment` is captured at mount so the
// imperative invalidate path can refetch without a React render; `seq` is the
// latest-start-wins guard for overlapping fetches.
const store: { fetchedAt: number; environment: any; seq: number } = {
  fetchedAt: 0,
  environment: null,
  seq: 0,
};

const REFRESH_TTL_MS = 60_000;

// Exported so the dashboard's endAbTest / reopenAbTest mutations can force an
// IMMEDIATE refetch from their onCompleted callbacks — resetting fetchedAt alone
// wouldn't refetch until the next tab-focus, leaving the overlay stale. No-op
// until the provider has mounted (environment captured).
export function invalidateAbTestResolutions() {
  store.fetchedAt = 0;
  if (store.environment) fetchResolutions(store.environment);
}

async function fetchResolutions(environment: any) {
  // Latest-start-wins: a slow in-flight fetch resolving AFTER a newer one must
  // not clobber the newer overlay with stale (e.g. pre-end) data.
  const mySeq = ++store.seq;
  try {
    const data = await fetchQuery<ABTestResolutionProviderQuery>(
      environment,
      RESOLUTIONS_QUERY,
      {},
    ).toPromise();
    if (mySeq !== store.seq) return; // superseded by a newer fetch
    abTestingService.applyServerOverlays(
      (data?.abTestResolutions ?? []).map((r) => ({
        testId: r.testId,
        resolvedVariant: r.resolvedVariant,
      })),
    );
    store.fetchedAt = Date.now();
  } catch {
    // Network blip — keep whatever overlay we have (fail open to live hashing).
  }
}

export function ABTestResolutionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const environment = useRelayEnvironment();

  useEffect(() => {
    // Capture for the imperative invalidate path (mutation onCompleted).
    store.environment = environment;
    if (store.fetchedAt === 0) {
      fetchResolutions(environment);
    }

    const onVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - store.fetchedAt > REFRESH_TTL_MS
      ) {
        fetchResolutions(environment);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () =>
      document.removeEventListener("visibilitychange", onVisibility);
  }, [environment]);

  return <>{children}</>;
}
